import {
	Box,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	Collapse,
	IconButton,
	makeStyles,
	MenuItem,
	Tooltip,
	Typography
} from '@material-ui/core'
import { MARKETING_CONTENT_STATUS } from '../../../utils/marketing'
import {
	ArrowDownward,
	ArrowUpward,
	CloudUpload,
	DeleteOutline,
	ExpandLessOutlined,
	ExpandMoreOutlined
} from '@material-ui/icons'
import React, { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import {
	createMarketingContent,
	deleteMarketingContent,
	updateMarketingContent,
	uploadMarketingContentImage
} from '../../../actions/facilities/marketingContent.actions'
import { DangerButton } from '../../Button/DangerButton'
import { MainButton } from '../../Button/MainButton'
import DangerConfirmationDialog from '../../Common/Dialogs/DangerConfirmation'
import StandardConfirmationDialog from '../../Common/Dialogs/StandardConfirmation'
import StyledAlert from '../../Common/StyledAlert'
import StyledChip from '../../Common/StyledChip'
import StyledSelect from '../../Common/StyledSelect'
import StyledSwitch from '../../Common/StyledSwitch'
import StyledTextField from '../../Common/StyledTextField'
import { FONT_WEIGHTS } from '../../../utils'
import { dangers, gray, purple } from '../../../utils/colors'
import {
	artworkDoesNotMeetRequirements,
	blockingReason,
	CATEGORY_CONFIG,
	CONTENT_FIELDS,
	isNewRecord
} from './marketingContentConfig'
import MarketingContentPreview from './MarketingContentPreview'
import { IN_APP_DESTINATIONS, screensForStack } from './inAppDestinations'

const useStyles = makeStyles(() => ({
	card: {
		marginBottom: 16,
		// Icons render invisible without an explicit fill in this app; scoped to IconButtons so
		// the buttons keep control of their own icon colours.
		'& .MuiIconButton-root .MuiSvgIcon-root': {
			color: purple[800],
			fill: purple[800]
		},
		'& .MuiIconButton-root.Mui-disabled .MuiSvgIcon-root': {
			color: gray[500],
			fill: gray[500]
		},
		// Without this, button icons inherit a stray white fill from the theme and disappear;
		// make them follow their button's own text colour.
		'& .MuiButton-root .MuiSvgIcon-root': {
			color: 'inherit',
			fill: 'currentColor'
		}
	},
	fileInput: { display: 'none' },
	// Matches StyledCollapsibleCard's height so a collapsed item lines up with sibling cards.
	header: {
		padding: '13px 24px',
		userSelect: 'none',
		cursor: 'pointer',
		'& .MuiCardHeader-action': {
			margin: 0,
			alignSelf: 'center'
		}
	},
	headerTitle: { display: 'flex', alignItems: 'center', gap: 8 },
	headerTitleText: {
		fontSize: 16,
		lineHeight: '22px',
		fontWeight: 600,
		textTransform: 'none'
	},
	headerActions: { display: 'flex', alignItems: 'center', gap: 4 },
	activeRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: 12,
		marginTop: 8,
		marginBottom: 24
	},
	grid: {
		display: 'grid',
		gridGap: '23px',
		marginBottom: '23px',
		alignItems: 'start',
		gridTemplateColumns: 'repeat(auto-fit, minmax(274px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: 'unset !important'
			}
		}
	},
	column: {
		display: 'flex',
		flexDirection: 'column',
		gap: '23px',
		minWidth: 0
	},
	sectionLabel: {
		display: 'block',
		fontFamily: 'Nunito',
		fontSize: '0.75rem',
		color: gray[900],
		textTransform: 'uppercase',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		letterSpacing: '0.0625rem',
		marginTop: 16,
		marginBottom: 8
	},
	missing: { color: dangers[600], fontStyle: 'italic' },
	actions: { display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' },
	deleteSlot: { marginLeft: 'auto' }
}))

/** Reads a chosen file's pixel dimensions in the browser, or null if it can't be read. */
const readImageSize = (file) =>
	new Promise((resolve) => {
		const url = URL.createObjectURL(file)
		const img = new Image()

		img.onload = () => {
			resolve({ width: img.naturalWidth, height: img.naturalHeight })
			URL.revokeObjectURL(url)
		}
		img.onerror = () => {
			resolve(null)
			URL.revokeObjectURL(url)
		}

		img.src = url
	})

/**
 * One marketing content record: its fields, an in-app preview, and the controls that act on it.
 *
 * Not built on StyledCollapsibleCard: that owns its own expanded state and reserves the header
 * action slot for the chevron, whereas this header carries the move/active controls and a new
 * record must open expanded.
 */
const MarketingContentItem = (props) => {
	const { record, facilityId, category, index, total, onSaved, onRemoved, onMove, disabled, defaultExpanded } =
		props
	const classes = useStyles()
	const dispatch = useDispatch()

	const config = CATEGORY_CONFIG[category]
	const isNew = isNewRecord(record)

	const [draft, setDraft] = useState(record)
	const [expanded, setExpanded] = useState(isNew || defaultExpanded)
	const [saving, setSaving] = useState(false)
	const [uploading, setUploading] = useState(false)
	const [confirmingDelete, setConfirmingDelete] = useState(false)
	const [confirmingActive, setConfirmingActive] = useState(false)
	const [artworkError, setArtworkError] = useState(null)
	const fileInputRef = useRef(null)
	// Unique per card — multiple cards render this input and duplicate DOM ids are invalid.
	const uploadInputId = `marketing-content-upload-${record._id || `new-${category}`}`

	// Only the editable content fields count as unsaved work. Exclude active / order / timestamps
	const dirty = CONTENT_FIELDS.some((field) => (draft[field] ?? '') !== (record[field] ?? ''))
	const isActive = record.status === MARKETING_CONTENT_STATUS.ACTIVE
	// Against the saved record, not the draft — activating writes through immediately, so
	// unsaved edits aren't what the toggle acts on.
	const blocked = isActive ? null : blockingReason(category, record)

	const setField = (field, value) => setDraft({ ...draft, [field]: value })

	const upload = async (event) => {
		const file = event.target.files && event.target.files[0]
		if (!file) {
			return
		}

		// Only the browser can check dimensions — uploads go straight to S3. Reject a file that
		// fails its requirements before it is uploaded or set on the draft.
		const size = await readImageSize(file)
		const problem = size ? artworkDoesNotMeetRequirements(category, size.width, size.height) : null

		setArtworkError(problem)

		if (problem) {
			if (fileInputRef.current) {
				fileInputRef.current.value = null
			}
			return
		}

		setUploading(true)
		const url = await dispatch(uploadMarketingContentImage(facilityId, file))
		setUploading(false)
		// Held in the draft only — nothing persists until Save, so a cancelled edit leaves
		// nothing behind.
		if (url) {
			setField(config.imageField, url)
		}
		if (fileInputRef.current) {
			fileInputRef.current.value = null
		}
	}

	// A UI-added record has no id, so its first Save is a create — not create-on-add, since a
	// blank record has no artwork and can't be active without it.
	const save = async () => {
		// Content fields only — active/order are server-owned (toggle, reorder), so sending the
		// draft's copies would revert them (toggle active on, then Save turns it back off).
		const payload = CONTENT_FIELDS.reduce((acc, field) => ({ ...acc, [field]: draft[field] }), {})

		setSaving(true)
		const saved = isNew
			? await dispatch(createMarketingContent(facilityId, { ...payload, category }))
			: await dispatch(updateMarketingContent(facilityId, record._id, payload))
		setSaving(false)

		if (saved) {
			// Re-sync to the persisted record so server-side normalisation doesn't leave the card
			// dirty. New records remount on their new id; an updated record keeps its key, so
			// reset here.
			setDraft(saved)
			onSaved(saved)
		}
	}

	const toggleActive = async () => {
		setConfirmingActive(false)
		// Written straight through, not via the draft — it reindexes the category server-side,
		// so the list refreshes from the response.
		const saved = await dispatch(
			updateMarketingContent(facilityId, record._id, { status: isActive ? MARKETING_CONTENT_STATUS.INACTIVE : MARKETING_CONTENT_STATUS.ACTIVE })
		)

		if (saved) {
			onSaved(saved, { reordered: true })
		}
	}

	const softDelete = async () => {
		setConfirmingDelete(false)
		const removed = await dispatch(deleteMarketingContent(facilityId, record._id))

		if (removed) {
			onRemoved(removed)
		}
	}

	// Clear the screen when the stack changes — it belonged to the old stack.
	const setStack = (stack) => setDraft({ ...draft, in_app_link_stack: stack, in_app_link_screen: '' })

	const renderField = (field) => {
		if (field.kind === 'inAppStack') {
			return (
				<StyledSelect
					label={field.label}
					value={draft.in_app_link_stack || ''}
					disabled={disabled}
					onChange={(e) => setStack(e.target.value)}>
					<MenuItem value="">None</MenuItem>
					{IN_APP_DESTINATIONS.map((destination) => (
						<MenuItem key={destination.stack} value={destination.stack}>
							{destination.label}
						</MenuItem>
					))}
				</StyledSelect>
			)
		}

		if (field.kind === 'inAppScreen') {
			const screens = screensForStack(draft.in_app_link_stack)

			return (
				<StyledSelect
					label={field.label}
					value={draft.in_app_link_screen || ''}
					disabled={disabled || !screens.length}
					onChange={(e) => setField('in_app_link_screen', e.target.value)}>
					<MenuItem value="">None</MenuItem>
					{screens.map((screen) => (
						<MenuItem key={screen.name} value={screen.name}>
							{screen.label}
						</MenuItem>
					))}
				</StyledSelect>
			)
		}

		return (
			<StyledTextField
				label={field.label}
				variant="filled"
				fullWidth
				multiline={field.multiline}
				rows={field.multiline ? 3 : undefined}
				value={draft[field.name] || ''}
				disabled={disabled}
				onChange={(e) => setField(field.name, e.target.value)}
			/>
		)
	}

	const artwork = draft[config.imageField]

	return (
		<Card className={classes.card}>
			<CardHeader
				className={classes.header}
				onClick={() => setExpanded(!expanded)}
				title={
					<Box className={classes.headerTitle}>
						<Typography className={classes.headerTitleText}>
							{draft.headline || <span className={classes.missing}>Untitled</span>}
						</Typography>
						{isActive ? null : record.status === MARKETING_CONTENT_STATUS.DRAFT ? (
							<StyledChip skiptownvariant="gray" size="small" label="Draft" />
						) : (
							<StyledChip skiptownvariant="gray" size="small" label="Inactive" />
						)}
						{dirty ? (
							<StyledChip skiptownvariant="warnings" size="small" label="Unsaved Data" />
						) : null}
					</Box>
				}
				action={
					<Box className={classes.headerActions}>
						{/* stopPropagation so moving does not also toggle the card. */}
						<Tooltip title="Move up">
							<span>
								<IconButton
									size="small"
									disabled={disabled || index === 0 || !isActive}
									onClick={(e) => {
										e.stopPropagation()
										onMove(index, index - 1)
									}}>
									<ArrowUpward fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Move down">
							<span>
								<IconButton
									size="small"
									disabled={disabled || index === total - 1 || !isActive}
									onClick={(e) => {
										e.stopPropagation()
										onMove(index, index + 1)
									}}>
									<ArrowDownward fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>

						{/* Grey rather than the card's purple, matching StyledCollapsibleCard. */}
						<IconButton size="small">
							{expanded ? (
								<ExpandLessOutlined style={{ fill: gray[700] }} />
							) : (
								<ExpandMoreOutlined style={{ fill: gray[700] }} />
							)}
						</IconButton>
					</Box>
				}
			/>

			<Collapse in={expanded} timeout="auto" unmountOnExit>
				<CardContent>
					{/* A record can be saved incomplete but not go live that way — the API enforces
					    it; the tooltip says why. */}
					<Box className={classes.activeRow}>
						<Typography variant="body2" color="textSecondary">
							{isActive ? 'Active in the app' : blocked || 'Hidden from the app'}
						</Typography>
						<Tooltip title={blocked || ''}>
							<span>
								<StyledSwitch
									checked={isActive}
									onChecked={() => setConfirmingActive(true)}
									disabled={disabled || isNew || Boolean(blocked)}
								/>
							</span>
						</Tooltip>
					</Box>

					<Box className={classes.grid}>
						{config.columns.map((column, columnIndex) => (
							<div key={columnIndex} className={classes.column}>
								{column.map((field) => (
									<div key={field.name} className="field-wrapper">
										{renderField(field)}
									</div>
								))}
							</div>
						))}
					</Box>

					<Typography className={classes.sectionLabel}>
						{config.imageLabel} · min size: {config.minWidth} x {config.minHeight}
					</Typography>

					<Box className={classes.actions}>
						<input
							className={classes.fileInput}
							id={uploadInputId}
							type="file"
							accept="image/*"
							ref={fileInputRef}
							onChange={upload}
						/>
						<MainButton
							label={artwork ? 'Replace' : 'Upload'}
							startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
							onClick={() => fileInputRef.current && fileInputRef.current.click()}
							disabled={disabled || uploading}
						/>
						{artworkError ? (
							<StyledAlert type="error" text={artworkError} style={{ flex: 1 }} />
						) : artwork ? (
							<Typography variant="caption" color="textSecondary">
								Uploaded
							</Typography>
						) : (
							<Typography variant="caption" color="textSecondary">
								Needed before this can go live
							</Typography>
						)}
					</Box>

					<Typography className={classes.sectionLabel}>Preview</Typography>
					<MarketingContentPreview category={category} draft={draft} />

					<Box className={classes.actions}>
						<MainButton
							label={saving ? 'Saving…' : 'Save'}
							variant="contained"
							onClick={save}
							disabled={disabled || saving || !dirty}
						/>
						<MainButton
							label="Cancel"
							onClick={() => setDraft(record)}
							disabled={disabled || saving || !dirty}
						/>
						{dirty ? (
							<Typography variant="caption" color="textSecondary">
								Unsaved changes
							</Typography>
						) : null}

						{/* A new record exists only in local state — nothing to soft-delete. */}
						{!isNew ? (
							<span className={classes.deleteSlot}>
								<DangerButton
									label="Delete"
									startIcon={<DeleteOutline />}
									onClick={() => setConfirmingDelete(true)}
									disabled={disabled || saving}
								/>
							</span>
						) : null}
					</Box>
				</CardContent>
			</Collapse>

			<DangerConfirmationDialog
				open={confirmingDelete}
				title="Delete content item"
				description={`"${
					record.headline || 'This record'
				}" will be removed and will no longer appear in the list of items.`}
				onConfirmDangerousAction={softDelete}
				onCancel={() => setConfirmingDelete(false)}
			/>

			<StandardConfirmationDialog
				open={confirmingActive}
				title={isActive ? 'Hide from the app' : 'Make active'}
				description={
					isActive
						? `"${
								record.headline || 'This record'
						  }" will stop appearing in the app straight away, and the items below it will move up to close the gap.`
						: config.allowMultiple
						? `"${
								record.headline || 'This record'
						  }" will start appearing in the app straight away, at the end of its section.`
						: `"${
								record.headline || 'This record'
						  }" will start appearing in the app straight away. Any other active item in this section will be deactivated, since only one can be active at a time.`
				}
				confirmButtonText={isActive ? 'Hide it' : 'Make active'}
				onConfirm={toggleActive}
				onCancel={() => setConfirmingActive(false)}
			/>
		</Card>
	)
}

export default MarketingContentItem
